// CreditKarmaCredentials — staff-only card on the client Overview tab for
// saving each funding client's Credit Karma login. CK is NOT a headless
// import provider (Intuit NuData blocks automation), so these creds are for
// the operator's own real-browser login + credit-report capture extension. The
// password is write-only from the UI: it is encrypted server-side and only
// comes back through the audited reveal endpoint.
import { Eye, EyeOff, KeyRound, Loader2, Pencil, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi, type Client } from "../lib/api";
import { CopyButton } from "../lib/pii";

export function CreditKarmaCredentials({
  client,
  onSaved,
}: {
  client: Client;
  onSaved: (updated: Client) => void;
}) {
  const onFile = !!client.has_credit_karma_credentials;
  const [editing, setEditing] = useState(!onFile);
  const [email, setEmail] = useState(client.credit_karma_login_email || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reveal state
  // Revealed creds tagged with the client id; `shown` gates rendering so a
  // revealed password can never appear against another client.
  const [revealed, setRevealed] = useState<{ forId: string; email: string | null; password: string | null } | null>(null);
  const shown = revealed && revealed.forId === client.id ? revealed : null;
  const [revealing, setRevealing] = useState(false);
  const hideTimer = useRef<number | null>(null);
  // Latest rendered client id — discard a reveal/save response that lands
  // after the host switched clients (this instance is reused).
  const idRef = useRef(client.id);
  idRef.current = client.id;

  // Reset on client switch so a revealed password can't bleed onto another
  // client's page; clear the auto-hide timer on unmount.
  useEffect(() => {
    setRevealed(null);
    setEditing(!client.has_credit_karma_credentials);
    setEmail(client.credit_karma_login_email || "");
    setPassword("");
    setErr(null);
    setRevealing(false);
    setSaving(false);
    if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
  }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (hideTimer.current) window.clearTimeout(hideTimer.current); }, []);

  async function save() {
    setErr(null);
    if (!email.trim()) { setErr("Email is required"); return; }
    if (!onFile && !password.trim()) { setErr("Enter the Credit Karma password"); return; }
    const requestedId = client.id;
    setSaving(true);
    try {
      const r = await clientsApi.saveCreditKarma(requestedId, {
        email: email.trim(),
        // Trim only decides presence; the password keeps its exact characters.
        password: password.trim() ? password : undefined,
      });
      if (idRef.current !== requestedId) return; // switched clients mid-save — don't clobber
      setPassword("");
      setEditing(false);
      setRevealed(null);
      onSaved(r.client);
    } catch (e: any) {
      if (idRef.current !== requestedId) return;
      setErr(e?.message || "Could not save");
    } finally {
      if (idRef.current === requestedId) setSaving(false);
    }
  }

  async function reveal() {
    if (shown) {
      setRevealed(null);
      if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
      return;
    }
    const requestedId = client.id;
    setRevealing(true);
    setErr(null);
    try {
      const r = await clientsApi.revealCreditKarma(requestedId);
      if (idRef.current !== requestedId) return; // switched clients mid-reveal — discard
      setRevealed(r.value ? { forId: requestedId, email: r.value.email, password: r.value.password } : { forId: requestedId, email: null, password: null });
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setRevealed(null), 30_000);
    } catch (e: any) {
      if (idRef.current !== requestedId) return;
      setErr(e?.message || "Could not reveal");
    } finally {
      if (idRef.current === requestedId) setRevealing(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h2><KeyRound size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> Credit Karma login</h2>
          <span className="muted small">
            Stored encrypted for this funding client. Used to log into their Credit Karma and run the capture — the password only appears when you reveal it.
          </span>
        </div>
        {onFile && !editing && <span className="pill pill-green"><ShieldCheck size={13} aria-hidden="true" /> On file</span>}
      </div>

      {err && <p className="error-text">{err}</p>}

      {!editing ? (
        <div className="profile-metrics">
          <div>
            <p>Email</p>
            <strong style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span>{shown?.email ?? client.credit_karma_login_email ?? "—"}</span>
              <CopyButton value={shown?.email ?? client.credit_karma_login_email ?? undefined} title="Copy email" />
            </strong>
          </div>
          <div>
            <p>Password</p>
            <strong style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span>{shown ? (shown.password || "—") : "••••••••••"}</span>
              <button
                type="button"
                className="revealable-toggle"
                onClick={reveal}
                disabled={revealing}
                title={shown ? "Hide" : "Reveal password (logged)"}
              >
                {revealing ? <Loader2 size={14} className="spin" /> : shown ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              {shown?.password && <CopyButton value={shown.password} title="Copy password" />}
            </strong>
          </div>
          {client.credit_karma_credentials_updated_at && (
            <div>
              <p>Saved</p>
              <strong style={{ fontSize: 14 }}>{String(client.credit_karma_credentials_updated_at).slice(0, 10)}</strong>
            </div>
          )}
          <div style={{ alignSelf: "end" }}>
            <button className="ghost-button" onClick={() => { setEditing(true); setPassword(""); }}>
              <Pencil size={14} aria-hidden="true" /> Update
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="form-row-2">
            <label className="form-field"><span>Credit Karma email *</span>
              <input type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@gmail.com" /></label>
            <label className="form-field"><span>{onFile ? "New password (leave blank to keep)" : "Password *"}</span>
              <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button className="primary-button" onClick={save} disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : <ShieldCheck size={14} aria-hidden="true" />} Save login
            </button>
            {onFile && (
              <button className="ghost-button" onClick={() => { setEditing(false); setPassword(""); setErr(null); }} disabled={saving}>
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
